import React, { useState } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Skeleton,
  Alert,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { DetailPanel } from 'components/shared/DetailPanel';
import { DetailPanelHeader } from 'components/shared/DetailPanelHeader';
import { ProspectInterface } from 'api/canprospect/prospect';
import GeneralInfoTab from './detail/GeneralInfoTab';
import ProspectHistoryTab from './detail/ProspectHistoryTab';
import ProspectEditForm from './detail/ProspectEditForm';

const STATUS_COLORS: Record<string, string> = {
  'New Prospect':      '#d7f7fc',
  'Contacted':         '#d7fce4',
  'Replied':           '#d7fce4',
  'Warm Lead':         '#f3fcd7',
  'Qualified Lead':    '#fcf6d7',
  'Proposal Stage':    '#fce6d7',
  'Negotiation Stage': '#fcd7d7',
  'Closed Won':        '#fcd7f1',
  'Closed Lost':       '#e6d7fc',
};

const formatAddress = (prospect: ProspectInterface): string => {
  let addr = prospect.Address || '';
  if (prospect.City) addr += ` - ${prospect.City} / ${prospect.Province || ''}`;
  if (prospect.PostalCode) addr += ` - ${prospect.PostalCode}`;
  return addr;
};

interface ProspectDetailPanelProps {
  open: boolean;
  onClose: () => void;
  prospect: ProspectInterface | null;
  loading: boolean;
  error: string | null;
  activeTab: number;
  onTabChange: (tab: number) => void;
  onRefetch: () => void;
}

const ProspectDetailPanel: React.FC<ProspectDetailPanelProps> = ({
  open,
  onClose,
  prospect,
  loading,
  error,
  activeTab,
  onTabChange,
  onRefetch,
}) => {
  const [editOpen, setEditOpen] = useState(false);

  const title = loading
    ? 'Loading...'
    : prospect?.CompanyName || 'No company name';

  const subtitle = loading ? '' : prospect ? formatAddress(prospect) : '';

  const status = prospect?.ProspectStatus?.trim() || '';
  const headerBgColor = STATUS_COLORS[status] || undefined;

  const handleEditSaved = () => {
    onRefetch();
  };

  return (
    <DetailPanel
      open={open}
      onClose={onClose}
      maxWidth="md"
      header={
        <DetailPanelHeader
          title={title}
          subtitle={subtitle}
          backgroundColor={headerBgColor}
          useWhiteText={false}
          onClose={onClose}
          chips={
            status ? (
              <Chip
                label={status}
                size="small"
                sx={{
                  bgcolor: STATUS_COLORS[status] || '#e0e0e0',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                }}
              />
            ) : undefined
          }
          actions={
            !loading && prospect ? (
              <IconButton
                onClick={() => setEditOpen(true)}
                aria-label="Edit prospect"
                size="small"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            ) : undefined
          }
        />
      }
    >
      {loading ? (
        <Box sx={{ p: 2.5 }}>
          <Skeleton variant="rectangular" height={40} sx={{ mb: 2, borderRadius: 1 }} />
          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
        </Box>
      ) : error ? (
        <Box sx={{ p: 2.5 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      ) : (
        <>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeTab}
              onChange={(_, newValue) => onTabChange(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="Prospect detail tabs"
              sx={{
                px: 2,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  minHeight: 48,
                },
              }}
            >
              <Tab label="General Info" id="prospect-tab-0" aria-controls="prospect-tabpanel-0" />
              <Tab label="Prospect History" id="prospect-tab-1" aria-controls="prospect-tabpanel-1" />
            </Tabs>
          </Box>

          <Box
            role="tabpanel"
            id={`prospect-tabpanel-${activeTab}`}
            aria-labelledby={`prospect-tab-${activeTab}`}
          >
            {activeTab === 0 && (
              <GeneralInfoTab
                prospect={prospect}
                loading={false}
                error={null}
              />
            )}
            {activeTab === 1 && (
              <ProspectHistoryTab
                prospectId={prospect?.Id ?? null}
                prospectStatusId={prospect?.ProspectStatusId ?? null}
              />
            )}
          </Box>

          {prospect && (
            <ProspectEditForm
              open={editOpen}
              onClose={() => setEditOpen(false)}
              prospect={prospect}
              onSaved={handleEditSaved}
            />
          )}
        </>
      )}
    </DetailPanel>
  );
};

export default ProspectDetailPanel;
